/**
 * Destroy-safe handoff between the sync engine (`SyncStateContext`) and the
 * `7punches:destroy` teardown handler in `useTokenRefresh.ts`.
 *
 * `useTokenRefresh` lives outside `SyncStateProvider` and can't consume its
 * context. This tiny module-level coordinator — mirroring the
 * `reportNetworkError` piggyback registry in `NetworkStatusContext.tsx` —
 * lets the destroy handler await the in-flight sync (bounded by a timeout)
 * without a provider dependency.
 */

let inFlightSync: Promise<unknown> | null = null;

/** The sync engine registers its in-flight batch request, or `null` when idle. */
export function setInFlightSync(promise: Promise<unknown> | null): void {
    inFlightSync = promise;
}

/**
 * Resolves when the current in-flight sync settles, or when `timeoutMs`
 * elapses — whichever comes first. Resolves immediately if nothing is in
 * flight.
 */
export function waitForInFlightSync(timeoutMs: number): Promise<void> {
    if (!inFlightSync) return Promise.resolve();

    return Promise.race([
        inFlightSync.then(
            () => undefined,
            () => undefined
        ),
        new Promise<void>(resolve => setTimeout(resolve, timeoutMs))
    ]);
}
